import {
    keepPreviousData,
    useMutation,
    useQuery,
    useQueryClient,
    type QueryClient,
} from '@tanstack/react-query';

import { creditApi, type LedgerFilters } from '@/entities/credit';
import { orderKeys } from '@/entities/order';
import {
    billingApi,
    invoiceApi,
    tariffApi,
    type InvoiceDraftChanges,
    type InvoiceFilters,
    type InvoiceStatusChange,
    type NewInvoice,
    type NewTariff,
    type TariffChanges,
} from '@/entities/invoice';
import { ulid } from '@/shared/api';

export const billingKeys = {
    invoicesAll: ['billing', 'invoices'] as const,
    invoices: (filters: InvoiceFilters) => ['billing', 'invoices', 'list', filters] as const,
    invoice: (invoiceUuid: string) => ['billing', 'invoices', 'one', invoiceUuid] as const,
    drafts: ['billing', 'invoices', 'drafts'] as const,
    summary: ['billing', 'summary'] as const,
    integration: ['billing', 'integration'] as const,
    tariffs: ['billing', 'tariffs'] as const,
    services: ['billing', 'services'] as const,
    wallet: ['credits', 'wallet'] as const,
    packages: ['credits', 'packages'] as const,
    ledgerAll: ['credits', 'ledger'] as const,
    ledger: (filters: LedgerFilters) => ['credits', 'ledger', filters] as const,
    payment: (paymentUuid: string) => ['billing', 'payment', paymentUuid] as const,
    paymentsAll: ['billing', 'payments'] as const,
    payments: (page: number) => ['billing', 'payments', page] as const,
    subscription: ['billing', 'subscription'] as const,
};

/** Hoeveel concepten één klik op "versturen" meeneemt: de grootste pagina die de API geeft. */
export const DRAFTS_PER_BATCH = 100;

/** Na terugkomst van de betaalpagina: zolang de betaling open is elke drie seconden kijken, een minuut lang. */
export const PAYMENT_POLL_INTERVAL_MS = 3_000;
export const PAYMENT_POLL_ATTEMPTS = 20;

export function useInvoices(filters: InvoiceFilters) {
    return useQuery({
        queryKey: billingKeys.invoices(filters),
        queryFn: () => invoiceApi.list(filters),
        placeholderData: keepPreviousData,
    });
}

/**
 * Eén factuur mét haar regels. De lijst stuurt die niet mee; het bewerkscherm en
 * de regeltabel onder een verstuurde factuur hebben ze nodig.
 */
export function useInvoice(invoiceUuid: string | null) {
    return useQuery({
        queryKey: billingKeys.invoice(invoiceUuid ?? ''),
        queryFn: () => invoiceApi.get(invoiceUuid ?? ''),
        enabled: invoiceUuid !== null,
    });
}

/** De concepten achter de knop "facturen versturen". Alleen voor wie mag versturen. */
export function useDraftInvoices(enabled: boolean) {
    return useQuery({
        queryKey: billingKeys.drafts,
        queryFn: () => invoiceApi.list({ status: 'draft', perPage: DRAFTS_PER_BATCH }),
        enabled,
    });
}

/** De omzet van de maand. Alleen op het tabblad met de facturen aan klanten. */
export function useMonthSummary(enabled = true) {
    return useQuery({
        queryKey: billingKeys.summary,
        queryFn: () => billingApi.monthSummary(),
        enabled,
    });
}

/** De koppeling met de boekhouding, waar de facturen aan klanten heen gaan. */
export function useBillingIntegration(enabled = true) {
    return useQuery({
        queryKey: billingKeys.integration,
        queryFn: () => billingApi.integration(),
        staleTime: 5 * 60 * 1_000,
        enabled,
    });
}

export function useTariffs() {
    return useQuery({ queryKey: billingKeys.tariffs, queryFn: () => tariffApi.list() });
}

export function useTariffServices(enabled: boolean) {
    return useQuery({
        queryKey: billingKeys.services,
        queryFn: () => tariffApi.services(),
        enabled,
        staleTime: 10 * 60 * 1_000,
    });
}

export function useWallet() {
    return useQuery({ queryKey: billingKeys.wallet, queryFn: () => creditApi.wallet() });
}

/** Het grootboek. Alleen inzien: corrigeren gebeurt met een tegenboeking. */
export function useLedger(filters: LedgerFilters) {
    return useQuery({
        queryKey: billingKeys.ledger(filters),
        queryFn: () => creditApi.transactions(filters),
        placeholderData: keepPreviousData,
    });
}

/**
 * Hoe een betaling ervoor staat, via `GET /credits/payments/{uuid}`.
 *
 * De server weet het (en vraagt een open betaling zelf na bij de provider):
 * geslaagd, mislukt, afgebroken, verlopen of nog open. Zolang de betaling open
 * is, kijkt dit een minuut lang elke drie seconden; daarna alleen nog op verzoek.
 * Een afgeronde betaling verandert niet meer, dus dan stopt het.
 */
export function usePaymentStatus(paymentUuid: string | null) {
    const client = useQueryClient();
    const queryKey = billingKeys.payment(paymentUuid ?? '');
    const payment = useQuery({
        queryKey,
        queryFn: () => creditApi.payment(paymentUuid ?? ''),
        enabled: paymentUuid !== null,
        refetchInterval: (query) =>
            query.state.data?.status === 'open' &&
            query.state.dataUpdateCount < PAYMENT_POLL_ATTEMPTS
                ? PAYMENT_POLL_INTERVAL_MS
                : false,
    });

    /** Hoe vaak er al gekeken is; na `PAYMENT_POLL_ATTEMPTS` keer met een open betaling stopt het kijken. */
    const checks = client.getQueryState(queryKey)?.dataUpdateCount ?? 0;

    return {
        payment,
        gaveUp: payment.data?.status === 'open' && checks >= PAYMENT_POLL_ATTEMPTS,
    };
}

/** Het abonnement bij Smartkeur: plan, deze maand, de volgende afschrijving en wat een keuring kost. */
export function useSubscription(enabled: boolean) {
    return useQuery({
        queryKey: billingKeys.subscription,
        queryFn: () => creditApi.subscription(),
        enabled,
    });
}

/** Wat het bedrijf aan Smartkeur betaalde, met de facturen. */
export function usePayments(page: number, enabled: boolean) {
    return useQuery({
        queryKey: billingKeys.payments(page),
        queryFn: () => creditApi.payments(page),
        enabled,
        placeholderData: keepPreviousData,
    });
}

/**
 * Na een betaling (of de terugkeer ervan) kan alles rond het geld anders zijn:
 * het abonnement, het tegoed, het grootboek en de betalingen.
 */
export function refreshAfterPayment(client: QueryClient): void {
    void client.invalidateQueries({ queryKey: billingKeys.subscription });
    void client.invalidateQueries({ queryKey: billingKeys.paymentsAll });
    refreshCredits(client);
}

/** Een abonnement kiezen: de eerste betaling openen. Daarna gaat het scherm naar de betaalpagina. */
export function useChooseSubscription() {
    return useMutation({
        mutationFn: (planCode: string) => creditApi.chooseSubscription(planCode),
    });
}

/** Opnieuw betalen na een mislukte incasso. */
export function usePayArrears() {
    return useMutation({ mutationFn: () => creditApi.payArrears() });
}

/** Keuringen kopen: een eenmalige betaling openen. */
export function useBuyInspections() {
    return useMutation({ mutationFn: (quantity: number) => creditApi.buyInspections(quantity) });
}

/** Wisselen, opzeggen of een opzegging terugdraaien: het antwoord is het nieuwe overzicht. */
export function useSubscriptionChange() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (
            change: { kind: 'switch'; planCode: string } | { kind: 'cancel' } | { kind: 'resume' },
        ) => {
            switch (change.kind) {
                case 'switch':
                    return creditApi.changeSubscription(change.planCode);
                case 'cancel':
                    return creditApi.cancelSubscription();
                default:
                    return creditApi.resumeSubscription();
            }
        },
        onSuccess: (overview) => client.setQueryData(billingKeys.subscription, overview),
    });
}

/** De tijdelijke link naar de PDF van een factuur van Smartkeur; per klik opnieuw, want hij verloopt. */
export function usePlatformInvoiceLink() {
    return useMutation({ mutationFn: (paymentUuid: string) => creditApi.invoiceLink(paymentUuid) });
}

function refreshInvoices(client: QueryClient): void {
    void client.invalidateQueries({ queryKey: billingKeys.invoicesAll });
    void client.invalidateQueries({ queryKey: billingKeys.summary });
}

/**
 * Een uitgegeven factuur zet de keuringen die zij factureert op Gefactureerd, en
 * haalt ze weg bij "te factureren": werklijst, dossier en dashboard lezen dan opnieuw.
 */
function refreshInvoicedOrders(client: QueryClient): void {
    refreshInvoices(client);
    void client.invalidateQueries({ queryKey: orderKeys.all });
    void client.invalidateQueries({ queryKey: ['dashboard'] });
}

export function refreshCredits(client: QueryClient): void {
    void client.invalidateQueries({ queryKey: billingKeys.wallet });
    void client.invalidateQueries({ queryKey: billingKeys.ledgerAll });
}

/**
 * Veel concepten in één actie. Een nieuwe idempotency key per klik: een herhaling
 * van hetzelfde verzoek na een weggevallen verbinding verstuurt niets dubbel.
 * Ook als het verzoek faalt kan een deel verstuurd zijn, dus de lijst ververst altijd.
 */
export function useSendInvoiceBatch() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (invoiceUuids: readonly string[]) =>
            invoiceApi.sendBatch({ invoiceUuids, idempotencyKey: ulid() }),
        onSettled: () => refreshInvoicedOrders(client),
    });
}

export function useSendInvoice() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (invoiceUuid: string) => invoiceApi.send(invoiceUuid),
        onSettled: () => refreshInvoicedOrders(client),
    });
}

/**
 * Een concept wijzigen. De factuur die terugkomt is de waarheid - inclusief de
 * herrekende totalen - dus die gaat meteen in de cache van deze ene factuur.
 */
export function useUpdateInvoice() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (input: { invoiceUuid: string; changes: InvoiceDraftChanges }) =>
            invoiceApi.update(input.invoiceUuid, input.changes),
        onSuccess: (invoice) => {
            client.setQueryData(billingKeys.invoice(invoice.uuid), invoice);
            refreshInvoices(client);
        },
    });
}

/**
 * Een nieuw concept opstellen, bijvoorbeeld met de regels van een gecrediteerde
 * factuur. Ook de keuring ververst: die staat daarna weer op "te factureren".
 */
export function useCreateInvoice() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (input: NewInvoice) => invoiceApi.create(input),
        onSettled: () => refreshInvoicedOrders(client),
    });
}

export function useChangeInvoiceStatus() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (input: {
            invoiceUuid: string;
            status: InvoiceStatusChange;
            reason?: string;
        }) =>
            invoiceApi.changeStatus(input.invoiceUuid, {
                status: input.status,
                reason: input.reason,
            }),
        onSettled: () => refreshInvoices(client),
    });
}

/** De tijdelijke link naar een PDF; per klik opnieuw, want hij verloopt. */
export function useInvoicePdfLink() {
    return useMutation({ mutationFn: (invoiceUuid: string) => invoiceApi.pdfLink(invoiceUuid) });
}

export function useCreateTariff() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (input: NewTariff) => tariffApi.create(input),
        onSuccess: () => void client.invalidateQueries({ queryKey: billingKeys.tariffs }),
    });
}

export function useUpdateTariff() {
    const client = useQueryClient();

    return useMutation({
        mutationFn: (input: { tariffUuid: string; changes: TariffChanges }) =>
            tariffApi.update(input.tariffUuid, input.changes),
        onSuccess: () => void client.invalidateQueries({ queryKey: billingKeys.tariffs }),
    });
}
